<h2 mat-dialog-title>
    <ng-container [ngSwitch]="currentStep">
        <span *ngSwitchCase="'file-select'">Import Configuration</span>
        <span *ngSwitchCase="'preview'">Review Changes</span>
        <span *ngSwitchCase="'importing'">Importing...</span>
        <span *ngSwitchCase="'complete'">Import Complete</span>
        <span *ngSwitchCase="'error'">Import Error</span>
    </ng-container>
</h2>

<mat-dialog-content>
    <!-- Step 1: File Selection -->
    <div *ngIf="currentStep === 'file-select'" class="step-content">
        <p class="step-description">
            Select a configuration ZIP file to import. This will preview the changes before applying them.
        </p>

        <div class="file-input-wrapper">
            <input type="file"
                   #fileInput
                   accept=".zip"
                   (change)="onFileSelected($event)"
                   style="display: none;">
            <button mat-flat-button
                    color="primary"
                    type="button"
                    (click)="fileInput.click()"
                    class="file-select-button">
                <mat-icon>upload_file</mat-icon>
                {{ selectedFile ? 'Change File' : 'Select ZIP File' }}
            </button>

            <div *ngIf="selectedFile" class="selected-file">
                <mat-icon>description</mat-icon>
                <span>{{ selectedFile.name }}</span>
                <span class="file-size">({{ (selectedFile.size / 1024).toFixed(1) }} KB)</span>
            </div>
        </div>

        <div *ngIf="errorMessage" class="error-message">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
        </div>
    </div>

    <!-- Step 2: Preview Changes -->
    <div *ngIf="currentStep === 'preview' && computedPreview" class="step-content preview-content">
        <!-- Integrity Warnings -->
        <div class="integrity-warnings" *ngIf="previewResult?.warnings?.length">
            <div class="integrity-warning" *ngFor="let warning of previewResult.warnings">
                <mat-icon svgIcon="attention" class="teller-online-icon teller-online-icon--caution"></mat-icon>
                <span>{{ warning }}</span>
            </div>
        </div>

        <div class="preview-header">
            <div class="tenant-info">
                <span class="label">Source:</span>
                <span class="value">{{ computedPreview.sourceTenantName }}</span>
                <span class="environment-name">({{ computedPreview.sourceEnvironmentName || 'Unknown' }})</span>
                <mat-icon>arrow_forward</mat-icon>
                <span class="label">Target:</span>
                <span class="value">{{ computedPreview.targetTenantName }}</span>
                <span class="environment-name">({{ computedPreview.targetEnvironmentName || 'Unknown' }})</span>
            </div>
            <div class="summary-badge" [class.no-changes]="hasNoChanges">
                {{ hasNoChanges ? 'No changes' : (totalChanges | plural: 'total change') }}
            </div>
        </div>

        <!-- No Changes Message -->
        <div *ngIf="hasNoChanges" class="no-changes-message">
            <mat-icon svgIcon="success" class="teller-online-icon teller-online-icon--success"></mat-icon>
            <h3>No Changes Detected</h3>
            <p>The configuration in this ZIP file matches the current configuration. There is nothing to import.</p>
        </div>

        <!-- Site Properties -->
        <div class="preview-section" *ngIf="computedPreview.siteChanges?.length">
            <h4>Site Properties</h4>
            <div class="property-changes">
                <div class="property-row" *ngFor="let change of computedPreview.siteChanges">
                    <span class="property-name">{{ change.name }}:</span>
                    <ng-container *ngIf="change.hasChanged; else noSiteChange">
                        <span class="property-current">{{ change.currentValue }}</span>
                        <mat-icon>arrow_forward</mat-icon>
                        <span class="property-incoming">{{ change.incomingValue }}</span>
                    </ng-container>
                    <ng-template #noSiteChange>
                        <span class="property-unchanged">{{ change.currentValue }}</span>
                        <span class="change-badge small change-unchanged">No changes</span>
                    </ng-template>
                </div>
            </div>
        </div>

        <!-- Teller Connections -->
        <div class="preview-section" *ngIf="computedPreview.tellerConnections?.length">
            <h4>Teller Connections</h4>
            <mat-accordion class="entity-accordion" multi>
                <mat-expansion-panel *ngFor="let connection of computedPreview.tellerConnections" class="entity-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <span class="change-badge" [ngClass]="getChangeTypeClass(connection.changeType)">
                                {{ getChangeTypeLabel(connection.changeType) }}
                            </span>
                            <span class="entity-name">{{ connection.data.name || connection.data.tellerSystemCode }}</span>
                        </mat-panel-title>
                        <mat-panel-description>
                            <ng-container *ngIf="connection.changeType === ChangeType.Create">
                                <span class="change-count">{{ getTellerConnectionNewFieldCount(connection.data) | plural: 'new value' }}</span>
                            </ng-container>
                            <span class="change-count" *ngIf="connection.changeType !== ChangeType.Create && connection.propertyChanges?.length">
                                {{ connection.propertyChanges.length | plural: 'property change' }}
                            </span>
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <!-- Teller Connection Property Changes -->
                    <div class="property-changes" *ngIf="connection.propertyChanges?.length">
                        <div class="property-row" *ngFor="let change of connection.propertyChanges">
                            <span class="property-name">{{ change.name }}:</span>
                            <span class="property-current">{{ change.currentValue }}</span>
                            <mat-icon>arrow_forward</mat-icon>
                            <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                    </div>
                    <div *ngIf="!connection.propertyChanges?.length && connection.changeType === ChangeType.Create" class="new-entity-notice">
                        New teller connection will be created
                    </div>
                    <div *ngIf="!connection.propertyChanges?.length && connection.changeType === ChangeType.Unchanged" class="no-changes-notice">
                        No changes to teller connection properties
                    </div>
                    <div *ngIf="!connection.propertyChanges?.length && connection.changeType === ChangeType.Update" class="no-changes-notice">
                        No changes to teller connection properties (see Pages section for child changes)
                    </div>
                </mat-expansion-panel>
            </mat-accordion>
        </div>

        <!-- Pages -->
        <div class="preview-section" *ngIf="getTotalPageCount() > 0">
            <h4>Pages</h4>
            <mat-accordion class="entity-accordion" multi>
                <ng-container *ngFor="let connection of computedPreview.tellerConnections">
                    <mat-expansion-panel *ngFor="let page of connection.pages" class="entity-panel">
                        <mat-expansion-panel-header>
                            <mat-panel-title>
                                <span class="change-badge" [ngClass]="getChangeTypeClass(page.changeType)">
                                    {{ getChangeTypeLabel(page.changeType) }}
                                </span>
                                <span class="entity-name">{{ page.data.header || '(No header)' }}</span>
                            </mat-panel-title>
                            <mat-panel-description>
                                <span class="entity-details">{{ connection.data.name || connection.data.tellerSystemCode }}</span>
                                <span class="entity-details" *ngIf="page.data.tellerBusinessSystemNo">
                                    BSN: {{ page.data.tellerBusinessSystemNo }}
                                </span>
                                <ng-container *ngIf="page.changeType === ChangeType.Create">
                                    <span class="change-count">
                                        {{ getPageNewFieldCount(page.data) + getPageChildChangesCount(page) | plural: 'new value' }}
                                    </span>
                                </ng-container>
                                <span class="change-count" *ngIf="page.changeType !== ChangeType.Create && (page.propertyChanges?.length || getPageChildChangesCount(page) > 0)">
                                    {{ (page.propertyChanges?.length || 0) + getPageChildChangesCount(page) | plural: 'change' }}
                                </span>
                            </mat-panel-description>
                        </mat-expansion-panel-header>

                        <!-- Page Property Changes -->
                        <div class="property-changes" *ngIf="page.propertyChanges?.length">
                            <div class="property-row" *ngFor="let change of page.propertyChanges">
                                <span class="property-name">{{ change.name }}:</span>
                                <span class="property-current">{{ change.currentValue }}</span>
                                <mat-icon>arrow_forward</mat-icon>
                                <span class="property-incoming">{{ change.incomingValue }}</span>
                            </div>
                        </div>
                        <div *ngIf="!page.propertyChanges?.length && page.changeType === ChangeType.Create" class="new-entity-notice">
                            New page will be created
                        </div>
                        <div *ngIf="!page.propertyChanges?.length && page.changeType === ChangeType.Unchanged" class="no-changes-notice">
                            No changes to page properties
                        </div>

                        <!-- Child Entities Section -->
                        <div class="child-entities-section" *ngIf="hasPageChildEntities(page)">
                            <h5 class="child-entities-header">Child Entities</h5>

                            <!-- SearchTypes -->
                            <div class="child-entity-group" *ngIf="page.searchTypes?.length">
                                <mat-accordion class="child-accordion" multi>
                                    <mat-expansion-panel *ngFor="let searchType of page.searchTypes" class="child-entity-panel">
                                        <mat-expansion-panel-header>
                                            <mat-panel-title>
                                                <span class="change-badge small" [ngClass]="getChangeTypeClass(searchType.changeType)">
                                                    {{ getChangeTypeLabel(searchType.changeType) }}
                                                </span>
                                                <mat-icon class="entity-type-icon">search</mat-icon>
                                                <span class="entity-name">{{ searchType.data.label }}</span>
                                            </mat-panel-title>
                                            <mat-panel-description>
                                                <span class="entity-type-label">Search Type</span>
                                                <ng-container *ngIf="searchType.changeType === ChangeType.Create">
                                                    <span class="change-count">{{ getSearchTypeNewFieldCount(searchType.data) | plural: 'new value' }}</span>
                                                </ng-container>
                                                <span class="change-count" *ngIf="searchType.changeType !== ChangeType.Create && searchType.propertyChanges?.length">
                                                    {{ searchType.propertyChanges.length | plural: 'change' }}
                                                </span>
                                            </mat-panel-description>
                                        </mat-expansion-panel-header>

                                        <div class="property-changes" *ngIf="searchType.propertyChanges?.length">
                                            <div class="property-row" *ngFor="let change of searchType.propertyChanges">
                                                <span class="property-name">{{ change.name }}:</span>
                                                <span class="property-current">{{ change.currentValue }}</span>
                                                <mat-icon>arrow_forward</mat-icon>
                                                <span class="property-incoming">{{ change.incomingValue }}</span>
                                            </div>
                                        </div>

                                        <!-- Instruction Image -->
                                        <div class="instruction-image-section" *ngIf="searchType.instructionImage && searchType.instructionImage.changeType !== AssetChangeType.None">
                                            <span class="asset-badge" [ngClass]="{
                                                'asset-new': searchType.instructionImage.changeType === AssetChangeType.New,
                                                'asset-replace': searchType.instructionImage.changeType === AssetChangeType.Replace,
                                                'asset-unchanged': searchType.instructionImage.changeType === AssetChangeType.Unchanged
                                            }">
                                                <mat-icon *ngIf="searchType.instructionImage.changeType === AssetChangeType.New">add_circle</mat-icon>
                                                <mat-icon *ngIf="searchType.instructionImage.changeType === AssetChangeType.Replace">sync</mat-icon>
                                                <mat-icon *ngIf="searchType.instructionImage.changeType === AssetChangeType.Unchanged" svgIcon="success" class="teller-online-icon teller-online-icon--success"></mat-icon>
                                                Instruction Image
                                            </span>
                                            <span class="asset-change-type">
                                                <ng-container [ngSwitch]="searchType.instructionImage.changeType">
                                                    <ng-container *ngSwitchCase="AssetChangeType.New">New</ng-container>
                                                    <ng-container *ngSwitchCase="AssetChangeType.Replace">Will replace existing</ng-container>
                                                    <ng-container *ngSwitchCase="AssetChangeType.Unchanged">No changes</ng-container>
                                                </ng-container>
                                            </span>
                                        </div>

                                        <div *ngIf="!searchType.propertyChanges?.length && searchType.changeType === ChangeType.Create"
                                             class="new-entity-notice">
                                            New search type will be created
                                        </div>
                                        <div *ngIf="!searchType.propertyChanges?.length && searchType.changeType === ChangeType.Unchanged"
                                             class="no-changes-notice">
                                            No changes to search type properties
                                        </div>
                                    </mat-expansion-panel>
                                </mat-accordion>
                            </div>

                            <!-- PageItemTypes -->
                            <div class="child-entity-group" *ngIf="page.pageItemTypes?.length">
                                <mat-accordion class="child-accordion" multi>
                                    <mat-expansion-panel *ngFor="let itemType of page.pageItemTypes" class="child-entity-panel">
                                        <mat-expansion-panel-header>
                                            <mat-panel-title>
                                                <span class="change-badge small" [ngClass]="getChangeTypeClass(itemType.changeType)">
                                                    {{ getChangeTypeLabel(itemType.changeType) }}
                                                </span>
                                                <mat-icon class="entity-type-icon">inventory_2</mat-icon>
                                                <span class="entity-name">{{ itemType.data.label }}</span>
                                            </mat-panel-title>
                                            <mat-panel-description>
                                                <span class="entity-type-label">Page Item Type</span>
                                                <ng-container *ngIf="itemType.changeType === ChangeType.Create">
                                                    <span class="change-count">{{ getPageItemTypeNewFieldCount(itemType.data) | plural: 'new value' }}</span>
                                                </ng-container>
                                                <span class="change-count" *ngIf="itemType.changeType !== ChangeType.Create && itemType.propertyChanges?.length">
                                                    {{ itemType.propertyChanges.length | plural: 'change' }}
                                                </span>
                                            </mat-panel-description>
                                        </mat-expansion-panel-header>

                                        <div class="property-changes" *ngIf="itemType.propertyChanges?.length">
                                            <div class="property-row" *ngFor="let change of itemType.propertyChanges">
                                                <span class="property-name">{{ change.name }}:</span>
                                                <span class="property-current">{{ change.currentValue }}</span>
                                                <mat-icon>arrow_forward</mat-icon>
                                                <span class="property-incoming">{{ change.incomingValue }}</span>
                                            </div>
                                        </div>

                                        <!-- Image -->
                                        <div class="instruction-image-section" *ngIf="itemType.image && itemType.image.changeType !== AssetChangeType.None">
                                            <span class="asset-badge" [ngClass]="{
                                                'asset-new': itemType.image.changeType === AssetChangeType.New,
                                                'asset-replace': itemType.image.changeType === AssetChangeType.Replace,
                                                'asset-unchanged': itemType.image.changeType === AssetChangeType.Unchanged
                                            }">
                                                <mat-icon *ngIf="itemType.image.changeType === AssetChangeType.New">add_circle</mat-icon>
                                                <mat-icon *ngIf="itemType.image.changeType === AssetChangeType.Replace">sync</mat-icon>
                                                <mat-icon *ngIf="itemType.image.changeType === AssetChangeType.Unchanged" svgIcon="success" class="teller-online-icon teller-online-icon--success"></mat-icon>
                                                Image
                                            </span>
                                            <span class="asset-change-type">
                                                <ng-container [ngSwitch]="itemType.image.changeType">
                                                    <ng-container *ngSwitchCase="AssetChangeType.New">New</ng-container>
                                                    <ng-container *ngSwitchCase="AssetChangeType.Replace">Will replace existing</ng-container>
                                                    <ng-container *ngSwitchCase="AssetChangeType.Unchanged">No changes</ng-container>
                                                </ng-container>
                                            </span>
                                        </div>

                                        <div *ngIf="!itemType.propertyChanges?.length && itemType.changeType === ChangeType.Create"
                                             class="new-entity-notice">
                                            New page item type will be created
                                        </div>
                                        <div *ngIf="!itemType.propertyChanges?.length && itemType.changeType === ChangeType.Unchanged"
                                             class="no-changes-notice">
                                            No changes to page item type properties
                                        </div>
                                    </mat-expansion-panel>
                                </mat-accordion>
                            </div>

                            <!-- InboundRedirectSources -->
                            <div class="child-entity-group" *ngIf="page.inboundRedirectSources?.length">
                                <mat-accordion class="child-accordion" multi>
                                    <mat-expansion-panel *ngFor="let redirect of page.inboundRedirectSources" class="child-entity-panel">
                                        <mat-expansion-panel-header>
                                            <mat-panel-title>
                                                <span class="change-badge small" [ngClass]="getChangeTypeClass(redirect.changeType)">
                                                    {{ getChangeTypeLabel(redirect.changeType) }}
                                                </span>
                                                <mat-icon class="entity-type-icon">input</mat-icon>
                                                <span class="entity-name">{{ redirect.data.label || 'Redirect ' + redirect.data.sourceKey }}</span>
                                            </mat-panel-title>
                                            <mat-panel-description>
                                                <span class="entity-type-label">Inbound Redirect</span>
                                                <ng-container *ngIf="redirect.changeType === ChangeType.Create">
                                                    <span class="change-count">{{ getInboundRedirectSourceNewFieldCount(redirect.data) | plural: 'new value' }}</span>
                                                </ng-container>
                                                <span class="change-count" *ngIf="redirect.changeType !== ChangeType.Create && redirect.propertyChanges?.length">
                                                    {{ redirect.propertyChanges.length | plural: 'change' }}
                                                </span>
                                            </mat-panel-description>
                                        </mat-expansion-panel-header>

                                        <div class="property-changes" *ngIf="redirect.propertyChanges?.length">
                                            <div class="property-row" *ngFor="let change of redirect.propertyChanges">
                                                <span class="property-name">{{ change.name }}:</span>
                                                <span class="property-current">{{ change.currentValue }}</span>
                                                <mat-icon>arrow_forward</mat-icon>
                                                <span class="property-incoming">{{ change.incomingValue }}</span>
                                            </div>
                                        </div>
                                        <div *ngIf="!redirect.propertyChanges?.length && redirect.changeType === ChangeType.Create"
                                             class="new-entity-notice">
                                            New inbound redirect source will be created
                                        </div>
                                        <div *ngIf="!redirect.propertyChanges?.length && redirect.changeType === ChangeType.Unchanged"
                                             class="no-changes-notice">
                                            No changes to redirect properties
                                        </div>
                                    </mat-expansion-panel>
                                </mat-accordion>
                            </div>
                        </div>
                    </mat-expansion-panel>
                </ng-container>
            </mat-accordion>
        </div>

        <!-- Menu Items -->
        <div class="preview-section" *ngIf="computedPreview.menuItems?.length">
            <h4>Menu Items</h4>
            <mat-accordion class="entity-accordion" multi>
                <mat-expansion-panel *ngFor="let item of computedPreview.menuItems" class="entity-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                                {{ getChangeTypeLabel(item.changeType) }}
                            </span>
                            <span class="entity-name">{{ item.data.label }}</span>
                        </mat-panel-title>
                        <mat-panel-description>
                            <ng-container *ngIf="item.changeType === ChangeType.Create">
                                <span class="change-count">{{ getMenuItemNewFieldCount(item.data) | plural: 'new value' }}</span>
                            </ng-container>
                            <span class="change-count" *ngIf="item.changeType !== ChangeType.Create && item.propertyChanges?.length">
                                {{ item.propertyChanges.length | plural: 'change' }}
                            </span>
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <div class="property-changes" *ngIf="item.propertyChanges?.length">
                        <div class="property-row" *ngFor="let change of item.propertyChanges">
                            <span class="property-name">{{ change.name }}:</span>
                            <span class="property-current">{{ change.currentValue }}</span>
                            <mat-icon>arrow_forward</mat-icon>
                            <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Create" class="new-entity-notice">
                        New menu item will be created
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged" class="no-changes-notice">
                        No changes to menu item properties
                    </div>
                </mat-expansion-panel>
            </mat-accordion>
        </div>

        <!-- Email Templates -->
        <div class="preview-section" *ngIf="computedPreview.emailTemplates?.length">
            <h4>Email Templates</h4>
            <mat-accordion class="entity-accordion" multi>
                <mat-expansion-panel *ngFor="let item of computedPreview.emailTemplates" class="entity-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                                {{ getChangeTypeLabel(item.changeType) }}
                            </span>
                            <span class="entity-name">{{ item.data.subject || getEmailTemplateName(item.data.name) }}</span>
                        </mat-panel-title>
                        <mat-panel-description>
                            <ng-container *ngIf="item.changeType === ChangeType.Create">
                                <span class="change-count">{{ getEmailTemplateNewFieldCount(item.data) | plural: 'new value' }}</span>
                            </ng-container>
                            <span class="change-count" *ngIf="item.changeType !== ChangeType.Create && item.propertyChanges?.length">
                                {{ item.propertyChanges.length | plural: 'change' }}
                            </span>
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <div class="property-changes" *ngIf="item.propertyChanges?.length">
                        <div class="property-row" *ngFor="let change of item.propertyChanges">
                            <span class="property-name">{{ change.name }}:</span>
                            <span class="property-current">{{ change.currentValue }}</span>
                            <mat-icon>arrow_forward</mat-icon>
                            <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Create" class="new-entity-notice">
                        New email template will be created
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged" class="no-changes-notice">
                        No changes to email template
                    </div>
                </mat-expansion-panel>
            </mat-accordion>
        </div>

        <!-- SMS Templates -->
        <div class="preview-section" *ngIf="computedPreview.smsTemplates?.length">
            <h4>SMS Templates</h4>
            <mat-accordion class="entity-accordion" multi>
                <mat-expansion-panel *ngFor="let item of computedPreview.smsTemplates" class="entity-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                                {{ getChangeTypeLabel(item.changeType) }}
                            </span>
                            <span class="entity-name">{{ getSmsTemplateName(item.data.name) }}</span>
                        </mat-panel-title>
                        <mat-panel-description>
                            <ng-container *ngIf="item.changeType === ChangeType.Create">
                                <span class="change-count">{{ getSmsTemplateNewFieldCount(item.data) | plural: 'new value' }}</span>
                            </ng-container>
                            <span class="change-count" *ngIf="item.changeType !== ChangeType.Create && item.propertyChanges?.length">
                                {{ item.propertyChanges.length | plural: 'change' }}
                            </span>
                        </mat-panel-description>
                    </mat-expansion-panel-header>

                    <div class="property-changes" *ngIf="item.propertyChanges?.length">
                        <div class="property-row" *ngFor="let change of item.propertyChanges">
                            <span class="property-name">{{ change.name }}:</span>
                            <span class="property-current">{{ change.currentValue }}</span>
                            <mat-icon>arrow_forward</mat-icon>
                            <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Create" class="new-entity-notice">
                        New SMS template will be created
                    </div>
                    <div *ngIf="!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged" class="no-changes-notice">
                        No changes to SMS template
                    </div>
                </mat-expansion-panel>
            </mat-accordion>
        </div>

        <!-- Assets -->
        <div class="preview-section" *ngIf="computedPreview.assetChanges?.length">
            <h4>Assets</h4>
            <div class="asset-list">
                <div *ngFor="let asset of computedPreview.assetChanges" class="asset-item">
                    <span class="asset-badge" [ngClass]="{
                        'asset-new': asset.changeType === AssetChangeType.New,
                        'asset-replace': asset.changeType === AssetChangeType.Replace,
                        'asset-unchanged': asset.changeType === AssetChangeType.Unchanged
                    }">
                        <mat-icon *ngIf="asset.changeType === AssetChangeType.New">add_circle</mat-icon>
                        <mat-icon *ngIf="asset.changeType === AssetChangeType.Replace">sync</mat-icon>
                        <mat-icon *ngIf="asset.changeType === AssetChangeType.Unchanged" svgIcon="success" class="teller-online-icon teller-online-icon--success"></mat-icon>
                        {{ asset.name }}
                    </span>
                    <span class="asset-change-type">
                        <ng-container [ngSwitch]="asset.changeType">
                            <ng-container *ngSwitchCase="AssetChangeType.New">New</ng-container>
                            <ng-container *ngSwitchCase="AssetChangeType.Replace">Will replace existing</ng-container>
                            <ng-container *ngSwitchCase="AssetChangeType.Unchanged">No changes</ng-container>
                        </ng-container>
                    </span>
                </div>
            </div>
        </div>

        <div class="warning-message" *ngIf="!hasNoChanges">
            <mat-icon svgIcon="attention" class="teller-online-icon teller-online-icon--caution"></mat-icon>
            <span>This will replace existing configuration data. This action cannot be undone.</span>
        </div>
    </div>

    <!-- Step 3: Importing -->
    <div *ngIf="currentStep === 'importing'" class="step-content centered">
        <mat-spinner diameter="48"></mat-spinner>
        <p>Importing configuration...</p>
        <p class="sub-text">Please do not close this dialog.</p>
    </div>

    <!-- Step 4: Complete -->
    <div *ngIf="currentStep === 'complete'" class="step-content centered">
        <mat-icon svgIcon="success" class="teller-online-icon teller-online-icon--success success-icon"></mat-icon>
        <h3>Import Successful</h3>
        <p>The configuration has been imported successfully.</p>

        <div *ngIf="importResult?.summary" class="import-summary">
            <div class="summary-row">
                <span>Total Changes:</span>
                <span>{{ importResult.summary.totalChanges }} ({{ importResult.summary.totalPropertyChanges }} properties, {{ importResult.summary.totalAssetChanges }} assets)</span>
            </div>
            <ng-container *ngIf="importResult.summary.entitiesWithChanges">
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['TellerConnections']">
                    <span>Teller Connections:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['TellerConnections'] }} with changes</span>
                </div>
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['Pages']">
                    <span>Pages:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['Pages'] }} with changes</span>
                </div>
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['MenuItems']">
                    <span>Menu Items:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['MenuItems'] }} with changes</span>
                </div>
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['EmailTemplates']">
                    <span>Email Templates:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['EmailTemplates'] }} with changes</span>
                </div>
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['SMSTemplates']">
                    <span>SMS Templates:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['SMSTemplates'] }} with changes</span>
                </div>
                <div class="summary-row" *ngIf="importResult.summary.entitiesWithChanges['Assets']">
                    <span>Assets:</span>
                    <span>{{ importResult.summary.entitiesWithChanges['Assets'] }} changed</span>
                </div>
            </ng-container>
        </div>
    </div>

    <!-- Error State -->
    <div *ngIf="currentStep === 'error'" class="step-content centered">
        <mat-icon class="error-icon">error</mat-icon>
        <h3>Import Failed</h3>
        <div class="error-details">
            <pre>{{ errorMessage }}</pre>
        </div>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <ng-container [ngSwitch]="currentStep">
        <!-- File Selection Actions -->
        <ng-container *ngSwitchCase="'file-select'">
            <button mat-button mat-dialog-close>Cancel</button>
            <button mat-flat-button
                    color="primary"
                    [disabled]="!selectedFile || isLoading"
                    (click)="onPreview()">
                <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
                <span *ngIf="!isLoading">Preview Changes</span>
            </button>
        </ng-container>

        <!-- Preview Actions -->
        <ng-container *ngSwitchCase="'preview'">
            <button mat-button (click)="onBack()">Back</button>
            <button mat-button mat-dialog-close>{{ hasNoChanges ? 'Close' : 'Cancel' }}</button>
            <button mat-flat-button
                    color="warn"
                    *ngIf="!hasNoChanges"
                    (click)="onConfirmImport()">
                Confirm Import
            </button>
        </ng-container>

        <!-- Importing Actions -->
        <ng-container *ngSwitchCase="'importing'">
            <!-- No actions while importing -->
        </ng-container>

        <!-- Complete Actions -->
        <ng-container *ngSwitchCase="'complete'">
            <button mat-flat-button color="primary" (click)="onComplete()">Done</button>
        </ng-container>

        <!-- Error Actions -->
        <ng-container *ngSwitchCase="'error'">
            <button mat-button (click)="onBack()">Try Again</button>
            <button mat-button mat-dialog-close>Close</button>
        </ng-container>
    </ng-container>
</mat-dialog-actions>
